export type Role = "ADMIN" | "OPERATOR" | "VIEWER";

export interface SessionResponse {
  ok: boolean;
  user: { telegram_user_id: string; display_name: string; username: string };
  role: Role;
  permissions: string[];
  site: { site_id: string; display_name: string; telegram_group_id: string };
  stage: string;
}

export interface AreaState {
  partition_id: string;
  area: "EXTERNAL" | "INTERNAL";
  display_name: string;
  state: "ARMED" | "DISARMED";
  transition: string;
  health: string;
  button_action: "ARM" | "DISARM";
  button_label: string;
  zone_count: number;
  zones: number[];
}

export interface AlarmState {
  state_version: number;
  global_state: string;
  global_label: string;
  areas: AreaState[];
  incident_gate_enabled: boolean;
  updated_at_utc: string;
}

export interface PgmItem {
  pgm_id: "PGM01" | "PGM02" | "PGM03";
  state: "ON" | "OFF";
  pgm_version: number;
  auto_off_at_utc: string | null;
  reason: string;
  maximum_on_ms: number;
  physical_output: boolean;
}

export interface PgmState {
  schema: string;
  physical_output_allowed: boolean;
  relay_map?: Record<string, number>;
  safe_state: "OFF";
  pgms: PgmItem[];
  history_count: number;
}

export interface SirenItem {
  siren_id: "SIREN_MAIN";
  state: "ON" | "OFF";
  siren_version: number;
  auto_off_at_utc: string | null;
  reason: string;
  maximum_on_ms: number;
  physical_output: boolean;
}

export interface SirenState {
  schema: string;
  safe_state: "OFF";
  physical_output_allowed: boolean;
  relay?: number;
  siren: SirenItem;
  history_count: number;
}

export interface BypassZoneState {
  zone: number;
  status: "ACTIVE" | "RESTORED" | "EXPIRED";
  bypass_version: number;
  bypass_until_utc?: string | null;
  reason?: string;
  updated_at_utc?: string;
}

export interface BypassState {
  schema: string;
  mutation_scope: "LOGICAL_GATE_ONLY";
  physical_output_allowed: false;
  active: BypassZoneState[];
  zones?: BypassZoneState[];
  history_count: number;
}

export interface TroubleItem {
  trouble_id: string;
  source_type: string;
  resource_id: string;
  code: string;
  severity: "WARNING" | "CRITICAL";
  message: string;
  status: "OPEN" | "ACKNOWLEDGED" | "RESOLVED";
  occurrence_count: number;
  opened_at_utc?: string;
  updated_at_utc?: string;
}

export interface TroubleState {
  schema: string;
  physical_output_allowed: false;
  independent_of_bypass: true;
  independent_of_arming: true;
  active: TroubleItem[];
  counts: { total: number; open: number; acknowledged: number; resolved: number };
}

export interface QueueState {
  schema: string;
  physical_output_allowed: false;
  counts: Record<string, number>;
  recent: Array<{
    job_id: string;
    job_type: string;
    status: string;
    attempt_count: number;
    max_attempts: number;
    updated_at_utc: string;
    last_error?: string | null;
  }>;
}

export interface RegistrySummary {
  schema: string;
  registry_id: string;
  revision: number;
  sha256: string;
  resource_count?: number;
  output_count?: number;
  kinds?: Record<string, number>;
  functions?: Record<string, number>;
  modes?: Record<string, number>;
  states: Record<string, number>;
  mutation_enabled: false;
}

export interface CockpitState {
  schema: string;
  generated_at_utc: string;
  freshness: { status: string; max_age_seconds: number };
  overall_status: "HEALTHY" | "DEGRADED" | "CRITICAL";
  reasons: string[];
  core: { database_ok: boolean; replay_physical_output: string };
  incidents: { total: number; by_status: Record<string, number> };
  zone_actions: { total: number; by_status: Record<string, number>; by_decision: Record<string, number> };
  bypass: { active_count: number; history_count: number };
  troubles: { counts: TroubleState["counts"]; active: TroubleItem[] };
  queue: { counts: Record<string, number>; actionable_count: number };
  data_protection: {
    backup_freshness: string;
    backup_age_hours: number | null;
    last_backup: { backup_id: string; sha256: string; restore_drill_status: string } | null;
    storage: { capacity_status: string; free_bytes: number };
    live_database_overwrite_allowed: false;
  };
  protection_automation: { running: boolean; last_error: string | null; completed_jobs: number };
  external_watchdog: { status: string; freshness: string; age_seconds: number; restart_count_window: number; restart_limit: number };
  relay: {
    connected: boolean;
    all_off_confirmed: boolean;
    output_state_confirmed?: boolean;
    latched_relays?: number[];
    active_actions: number;
  };
  telegram: { ready: boolean; degraded: boolean; active_deliveries: number; delivery_counts: { total: number; by_status: Record<string, number> } };
}

export interface AlarmCommandIntent {
  action: "SET_ALL_AREAS" | "SET_AREA_STATE";
  area?: "EXTERNAL" | "INTERNAL";
  target_state: "ARMED" | "DISARMED";
  expected_state_version: number;
  reason?: string;
}

export interface BypassCommandIntent {
  action: "SET_ZONE_BYPASS" | "RESTORE_ZONE";
  zone: number;
  expected_bypass_version: number;
  reason: string;
  bypass_until?: string;
}

export interface PgmCommandIntent {
  action: "PGM_ON" | "PGM_OFF";
  pgm_id: PgmItem["pgm_id"];
  expected_pgm_version: number;
  duration_ms?: number;
  reason: string;
}

export interface SirenCommandIntent {
  action: "SIREN_ON" | "SIREN_OFF";
  siren_id: "SIREN_MAIN";
  expected_siren_version: number;
  duration_ms?: number;
  reason: string;
}

export interface CommandResponse<T> {
  ok: true;
  command_id: string;
  result: Record<string, unknown>;
  confirmed_state?: T;
  state_confirmation: "CONFIRMED" | "PENDING";
  correlation_id: string;
}

export interface ErrorPayload {
  error?: unknown;
  message?: unknown;
  current_state?: unknown;
  retryable?: boolean;
  correlation_id?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly currentState?: unknown;
  readonly retryable: boolean;
  readonly correlationId?: string;

  constructor(status: number, payload: ErrorPayload) {
    super(String(payload.message ?? payload.error ?? `HTTP ${status}`));
    this.name = "ApiError";
    this.status = status;
    this.code = String(payload.error ?? `HTTP_${status}`);
    this.currentState = payload.current_state;
    this.retryable = Boolean(payload.retryable);
    this.correlationId = payload.correlation_id;
  }
}

function initData(): string {
  return window.Telegram?.WebApp.initData ?? "";
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        "X-Telegram-Init-Data": initData(),
        ...(init?.body ? { "Content-Type": "application/json; charset=utf-8" } : {}),
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    throw new ApiError(0, { error: "NETWORK_UNAVAILABLE", message: "Sem comunicação com o Control Plane.", retryable: true });
  }

  let payload: ErrorPayload | T;
  try {
    payload = await response.json() as ErrorPayload | T;
  } catch {
    throw new ApiError(response.status, { error: "INVALID_JSON_RESPONSE", message: "Resposta inválida do Control Plane.", retryable: response.status >= 500 });
  }
  if (!response.ok) throw new ApiError(response.status, payload as ErrorPayload);
  return payload as T;
}

async function getSiteData<T>(siteId: string, route: string): Promise<T> {
  const response = await requestJson<{ ok: true; data: T }>(`/api/sites/${encodeURIComponent(siteId)}/${route}`);
  return response.data;
}

async function sendSiteCommand<TState, TIntent>(siteId: string, route: string, payload: TIntent): Promise<CommandResponse<TState>> {
  return requestJson<CommandResponse<TState>>(`/api/sites/${encodeURIComponent(siteId)}/${route}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export const getSession = (): Promise<SessionResponse> => requestJson<SessionResponse>("/api/session");

export async function getAlarmState(siteId: string): Promise<AlarmState> {
  const response = await requestJson<{ ok: true; state: AlarmState }>(`/api/sites/${encodeURIComponent(siteId)}/state`);
  return response.state;
}

export const getCockpit = (siteId: string): Promise<CockpitState> => getSiteData(siteId, "cockpit");
export const getPgms = (siteId: string): Promise<PgmState> => getSiteData(siteId, "pgms");
export const getSiren = (siteId: string): Promise<SirenState> => getSiteData(siteId, "siren");
export const getBypass = (siteId: string): Promise<BypassState> => getSiteData(siteId, "bypass");
export const getTroubles = (siteId: string): Promise<TroubleState> => getSiteData(siteId, "troubles");
export const getQueue = (siteId: string): Promise<QueueState> => getSiteData(siteId, "queue");
export const getResources = (siteId: string): Promise<RegistrySummary> => getSiteData(siteId, "resources");
export const getOutputs = (siteId: string): Promise<RegistrySummary> => getSiteData(siteId, "outputs");

export const sendAlarmCommand = (siteId: string, payload: AlarmCommandIntent): Promise<CommandResponse<AlarmState>> =>
  sendSiteCommand(siteId, "commands", payload);
export const sendBypassCommand = (siteId: string, payload: BypassCommandIntent): Promise<CommandResponse<BypassState>> =>
  sendSiteCommand(siteId, "bypass/commands", payload);
export const sendPgmCommand = (siteId: string, payload: PgmCommandIntent): Promise<CommandResponse<PgmState>> =>
  sendSiteCommand(siteId, "pgms/commands", payload);
export const sendSirenCommand = (siteId: string, payload: SirenCommandIntent): Promise<CommandResponse<SirenState>> =>
  sendSiteCommand(siteId, "siren/commands", payload);
export const acknowledgeTrouble = (siteId: string, troubleId: string, reason: string): Promise<CommandResponse<TroubleState>> =>
  sendSiteCommand(siteId, `troubles/${encodeURIComponent(troubleId)}/acknowledge`, { action: "ACKNOWLEDGE_TROUBLE", reason });
